import { BaseAudioSource } from "./audio_source_base.js";
import { createAudioContextAtRate, createResampler } from "./audio_resampler.js";

/**
 * MicAudioSource
 *
 * - 使用 getUserMedia 取得麥克風
 * - AudioContext 直接以 16 kHz 建立，由瀏覽器原生做抗混疊重採樣
 * - 瀏覽器不接受 16 kHz 時才退回 AntiAliasResampler（見 audio_resampler.js）
 * - 依 chunkSec 聚合後 emit chunk
 */
export class MicAudioSource extends BaseAudioSource {
  constructor() {
    super();

    // config
    this._targetSampleRate = 16000;
    this._chunkSec = 0.2;

    // audio graph
    this._audioCtx = null;
    this._mediaStream = null;
    this._sourceNode = null;
    this._processorNode = null; // ScriptProcessor fallback
    this._silentGain = null; // ScriptProcessor 靜音輸出用

    // buffer
    this._buffer = [];

    // state
    this._inputSampleRate = null;
    // 只有在瀏覽器不給 16 kHz context 時才不是 null；正常路徑是 pass-through
    this._resampler = null;

    // ready 後裝置仍可能被拔除、mute，或 AudioContext 被瀏覽器 suspend。
    // handler 固定 identity，才能在換裝置／destroy 時精確解除。
    this._onCaptureStateChange = null;
    this._captureTrack = null;
    this._contextStateHandler = () => this._emitCaptureState("context_statechange");
    this._trackEndedHandler = () => this._emitCaptureState("track_ended");
    this._trackMuteHandler = () => this._emitCaptureState("track_muted");
    this._trackUnmuteHandler = () => this._emitCaptureState("track_unmuted");
  }

  /**
   * 列出可用的收音裝置。
   *
   * ⚠️ 必須在 getUserMedia 取得授權之後才叫得出名字——未授權前瀏覽器一律回
   * 空字串 label（防指紋追蹤），畫面會變成一排「麥克風 1 / 2 / 3」。
   */
  static async listInputDevices() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return [];
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter((d) => d.kind === "audioinput")
      .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `麥克風 ${i + 1}` }));
  }

  /**
   * init
   * @param {Object} config
   * @param {number} [config.targetSampleRate=16000]
   * @param {number} [config.chunkSec=0.2]
   * @param {string} [config.deviceId] - 指定收音裝置；不給則用系統預設
   */
  async init(config = {}) {
    const { targetSampleRate = 16000, chunkSec = 0.2, deviceId = null } = config;

    this._targetSampleRate = targetSampleRate;
    this._chunkSec = chunkSec;

    // 直接以目標取樣率建立 AudioContext，讓瀏覽器原生做抗混疊重採樣。
    //
    // 舊版不指定 sampleRate（拿到裝置原生的 48 kHz），再用手寫的 downsample()
    // 抽點到 16 kHz——那支沒有低通，8 kHz 以上的能量全部折回語音帶，實測混疊比
    // 正規重採樣高 +24.28 dB、真實中文語音 6–8 kHz 被灌入 +6.68 dB 假能量，
    // 正好落在 ㄕ/ㄙ/ㄒ 這些擦音與醫學術語尾音（-osis / -itis 的 s）的辨識帶上。
    //
    // 這不是新機制：同 repo 的 test-steps/permission-microphone.vue 早就用
    // `new AudioContext({ sampleRate: 16000 })`，這裡只是把它搬到 ASR 主路徑。
    this._audioCtx =
      this._audioCtx || createAudioContextAtRate(this._targetSampleRate);

    // 🔴 **絕對不能 await 這個 resume()**。
    //
    // 瀏覽器的 autoplay policy：沒有 user activation（點擊 / 按鍵）時，
    // AudioContext 會以 suspended 建立，而 `resume()` 回的 promise 會**一直
    // pending、永遠不 settle**——不是 reject，是卡住。await 它等於整個 init()
    // 掛死，呼叫端的 loading 旗標永遠不會清掉、按鈕永遠 disabled。
    //
    // 原本這裡 await 是成立的，因為 init() 一律在按鈕的 click handler 裡呼叫
    // （手勢鏈內，resume 立刻成功）。EVAS2.0 改成「進頁面自動收音」之後這個前提
    // 就不存在了：從 Home 點 START EXAMINATION 進來還有 sticky activation，
    // 但直接貼網址 / F5 進來就沒有。
    //
    // 改成 fire-and-forget，並由呼叫端用 isSuspended() 判斷要不要提示使用者
    // 點一下畫面（見 resumeContext）。context 在 suspended 狀態下仍可建立
    // 節點，只是不會產生音訊 frame。
    if (this._audioCtx.state === "suspended") {
      this._audioCtx.resume().catch(() => {});
    }

    // 取得麥克風。
    //
    // deviceId 用 `exact`：內視鏡室常同時存在筆電內建麥克風與外接設備
    // （例如鏡頭 + 麥克風的複合 USB 裝置），不指定的話瀏覽器會拿系統預設，
    // 而系統預設往往不是醫師實際要用的那支。用 `ideal` 則在裝置不存在時
    // 靜默改用別支——收音來源被換掉卻沒有任何提示，比直接失敗更糟。
    //
    // echoCancellation / noiseSuppression / autoGainControl 一律關閉：
    // 那些是為了會議語音設計的，會削掉醫學術語的高頻細節，讓 ASR 更容易聽錯。
    const audioConstraints = {
      channelCount: 1,
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
    };
    if (deviceId) audioConstraints.deviceId = { exact: deviceId };

    this._mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: audioConstraints,
    });

    this._sourceNode = this._audioCtx.createMediaStreamSource(this._mediaStream);
    this._inputSampleRate = this._audioCtx.sampleRate;

    // 回讀而非假設：有些瀏覽器不丟 NotSupportedError、只是靜默忽略 sampleRate。
    // 取樣率已經是目標值時 resampler 為 null，_onAudioFrame 走 pass-through。
    this._resampler = createResampler(
      this._inputSampleRate,
      this._targetSampleRate
    );
    if (this._resampler) {
      console.warn(
        "[MicAudioSource] AudioContext 取樣率為 " +
          this._inputSampleRate +
          " Hz（要求 " +
          this._targetSampleRate +
          " Hz），改由 FIR 抗混疊重採樣器處理"
      );
    }

    // 建立 processor（worklet 載入是 async，必須等它完成）
    await this._setupProcessor();

    super.init(config);
    this._bindCaptureLifecycle();
  }

  /**
   * 目前實際收音的裝置名稱（給上方資訊列的「作用中麥克風」用）。
   * 未授權 / 未 init 時回空字串——呼叫端要能處理拿不到名字的情況。
   */
  getDeviceLabel() {
    const track = this._mediaStream && this._mediaStream.getAudioTracks()[0];
    return (track && track.label) || "";
  }

  /**
   * 實際採用的音訊處理節點類型（診斷用）。
   * 'worklet' | 'scriptprocessor' | null（尚未建立）
   */
  getProcessorKind() {
    if (!this._processorNode) return null;
    return typeof AudioWorkletNode !== "undefined" &&
      this._processorNode instanceof AudioWorkletNode
      ? "worklet"
      : "scriptprocessor";
  }

  /**
   * 送往 KKASR 的實際取樣率（診斷用）。這是 AudioContext 的取樣率，也就是
   * chunk 真正的取樣率；與下方 getTrackSettings() 回的「裝置端擷取取樣率」
   * 是兩件事（裝置常維持 48000，由瀏覽器重採樣到這個值）。
   */
  getOutputSampleRate() {
    return this._audioCtx ? this._audioCtx.sampleRate : null;
  }

  /**
   * 回讀麥克風 track **實際生效**的設定（診斷用）。
   *
   * echoCancellation / noiseSuppression / autoGainControl 三項是 constraint
   * 不是保證：macOS 的「語音隔離」、Windows 的裝置級增強、部分 USB 複合裝置的
   * 韌體 DSP 都在 getUserMedia 的管轄之外。現場出問題時要能一眼看出瀏覽器到底
   * 有沒有照做，所以顯示的必須是這裡回讀的值，不是我們寫死的 constraint 常數。
   *
   * 瀏覽器沒提供某個欄位時該欄回 null，呼叫端要能顯示「未提供」而不是假裝是 false。
   */
  getTrackSettings() {
    const track = this._mediaStream && this._mediaStream.getAudioTracks()[0];
    if (!track || typeof track.getSettings !== "function") return null;
    const settings = track.getSettings() || {};
    const read = (key) => (key in settings ? settings[key] : null);
    return {
      sampleRate: read("sampleRate"),
      echoCancellation: read("echoCancellation"),
      noiseSuppression: read("noiseSuppression"),
      autoGainControl: read("autoGainControl"),
    };
  }

  /** AudioContext 目前狀態（診斷用）：'running' | 'suspended' | 'closed' | null */
  getContextState() {
    return this._audioCtx ? this._audioCtx.state : null;
  }

  /**
   * AudioContext 是否還被 autoplay policy 擋著。
   * true 代表麥克風已取得、但音訊不會流動，直到使用者與頁面互動一次。
   */
  isSuspended() {
    return !!this._audioCtx && this._audioCtx.state === "suspended";
  }

  /**
   * ready gate 使用的實際收音狀態。BaseAudioSource._running 只代表呼叫過 start()；
   * AudioContext 已 suspend/close、USB track ended/muted 時都不能宣稱仍可口述。
   */
  isCaptureActive() {
    const track = this._mediaStream && this._mediaStream.getAudioTracks()[0];
    return !!(
      this.isInitialized()
      && this.isRunning()
      && this._audioCtx
      && this._audioCtx.state === "running"
      && this._mediaStream.active !== false
      && track
      && track.readyState === "live"
      && track.enabled !== false
      && track.muted !== true
    );
  }

  /** 註冊實際收音狀態事件；payload 不含裝置名稱或音訊內容。 */
  onCaptureStateChange(callback) {
    this._onCaptureStateChange = typeof callback === "function" ? callback : null;
  }

  /**
   * 在 user gesture 內呼叫，解除 autoplay policy 的封鎖。
   * @returns {Promise<boolean>} 是否已經 running
   */
  async resumeContext() {
    if (!this._audioCtx) return false;
    if (this._audioCtx.state === "running") return true;
    try {
      await this._audioCtx.resume();
    } catch (e) {
      return false;
    }
    return this._audioCtx.state === "running";
  }

  /** 目前實際在收音的 deviceId（下拉選單要標記選中項） */
  getDeviceId() {
    const track = this._mediaStream && this._mediaStream.getAudioTracks()[0];
    return (track && track.getSettings().deviceId) || "";
  }

  /**
   * start: 開始送 chunk（mic stream 本身已在跑）
   */
  start() {
    super.start();
    this._emitCaptureState("started");
    // mic stream 一直在跑，emit 由 processor callback 控制
  }

  /**
   * pause: 暫停送 chunk（mic 仍然開著）
   */
  pause() {
    super.pause();
  }

  /**
   * stop: 停止送 chunk（但不關 mic）
   *
   * 🔴 **清空 buffer 之前一定要先 flush**。chunk 要滿 0.2 秒才送出，直接清空
   * 等於把收尾最多 199 ms 的尾音永久丟掉——那不是靜音，是醫師最後一個字的尾音，
   * 也可能是「EVAS FINISH」前的收尾音節。File Mode 一直都有
   * _drainUnsentPcmBeforeEos()，Mic Mode 缺了對應處理。
   *
   * 呼叫端（Exam2DetailPage.stopAudio）在 signalEos() 之前呼叫 stop()，
   * 因此這裡送出的尾音仍在 EOS 之前抵達 KKASR。
   */
  stop() {
    this._flushTail();
    super.stop();
    this._buffer = [];
  }

  /**
   * destroy: 關閉 mic，釋放資源
   */
  destroy() {
    this._unbindCaptureLifecycle();
    this.stop();

    if (this._processorNode) {
      this._processorNode.disconnect();
      this._processorNode = null;
    }

    if (this._silentGain) {
      this._silentGain.disconnect();
      this._silentGain = null;
    }

    if (this._sourceNode) {
      this._sourceNode.disconnect();
      this._sourceNode = null;
    }

    if (this._mediaStream) {
      this._mediaStream.getTracks().forEach((t) => t.stop());
      this._mediaStream = null;
    }

    // 濾波器記憶屬於「這一場收音」，留著會讓下一場開頭混進上一場的尾音。
    this._resampler = null;

    if (this._audioCtx) {
      // 可選：若整個 app 都不用了再 close
      // this._audioCtx.close();
      // this._audioCtx = null;
    }

    super.destroy();
  }

  // =========================
  // Internal
  // =========================

  _emitCaptureState(reason) {
    if (!this._onCaptureStateChange) return;
    const track = this._mediaStream && this._mediaStream.getAudioTracks()[0];
    this._onCaptureStateChange({
      active: this.isCaptureActive(),
      reason,
      contextState: this.getContextState(),
      trackState: track ? track.readyState : null,
      trackMuted: track ? track.muted === true : null,
    });
  }

  _bindCaptureLifecycle() {
    this._unbindCaptureLifecycle();
    if (this._audioCtx && typeof this._audioCtx.addEventListener === "function") {
      this._audioCtx.addEventListener("statechange", this._contextStateHandler);
    }
    const track = this._mediaStream && this._mediaStream.getAudioTracks()[0];
    if (!track || typeof track.addEventListener !== "function") return;
    this._captureTrack = track;
    track.addEventListener("ended", this._trackEndedHandler);
    track.addEventListener("mute", this._trackMuteHandler);
    track.addEventListener("unmute", this._trackUnmuteHandler);
  }

  _unbindCaptureLifecycle() {
    if (this._audioCtx && typeof this._audioCtx.removeEventListener === "function") {
      this._audioCtx.removeEventListener("statechange", this._contextStateHandler);
    }
    const track = this._captureTrack;
    if (track && typeof track.removeEventListener === "function") {
      track.removeEventListener("ended", this._trackEndedHandler);
      track.removeEventListener("mute", this._trackMuteHandler);
      track.removeEventListener("unmute", this._trackUnmuteHandler);
    }
    this._captureTrack = null;
  }

  /**
   * 建立音訊處理節點。AudioWorklet 優先，失敗一律退回 ScriptProcessor。
   *
   * 🔴 **這個 try/catch 不是防禦性冗餘，是必要的**：
   *
   * `_setupWorklet()` 走 `audioWorklet.addModule(blob:...)`，而 **AudioWorklet
   * 模組受 CSP 的 `script-src` 管轄**。KKASR 自己 serve 的 dev.html 沒有 CSP，
   * 所以那邊一直沒事；kingcolon 的 client 由 nginx 送出
   * `script-src 'self'`（不含 `blob:`）→ addModule 被擋 → 整個 init() 拋錯 →
   * 麥克風看似取得了（getUserMedia 在這一步之前就成功）卻完全不能用。
   *
   * 不改 nginx 的 CSP 去放行 blob：那會為了一個音訊節點把整站的 script-src
   * 打開，代價與收益完全不成比例（見 kingcolon CLAUDE.md 對 CSP 的紅字警告：
   * 改 CSP 只動 connect-src）。
   *
   * ScriptProcessor 雖然已標記 deprecated，但所有現行瀏覽器都還支援，且它是
   * 純 JS API、不載入外部模組，不受 CSP 影響——這條 fallback 本來就存在，
   * 只是原本沒有人在 worklet 失敗時走到它。
   */
  async _setupProcessor() {
    if (this._audioCtx.audioWorklet) {
      try {
        await this._setupWorklet();
        return;
      } catch (err) {
        console.warn(
          "[MicAudioSource] AudioWorklet 不可用，改用 ScriptProcessor。" +
            "最常見原因是 CSP script-src 不放行 blob:（原因：" +
            ((err && err.message) || err) +
            "）"
        );
      }
    }
    this._setupScriptProcessor();
  }

  async _setupWorklet() {
    const workletCode = `
      class MicProcessor extends AudioWorkletProcessor {
        process(inputs) {
          const input = inputs[0];
          if (input && input[0]) {
            this.port.postMessage(input[0]);
          }
          return true;
        }
      }
      registerProcessor("mic-processor", MicProcessor);
    `;

    const blob = new Blob([workletCode], { type: "application/javascript" });
    const url = URL.createObjectURL(blob);
    await this._audioCtx.audioWorklet.addModule(url);

    const node = new AudioWorkletNode(this._audioCtx, "mic-processor");
    node.port.onmessage = (e) => {
      this._onAudioFrame(e.data);
    };

    this._sourceNode.connect(node);
    // 不接 destination（避免回音）
    this._processorNode = node;
  }

  _setupScriptProcessor() {
    const bufferSize = pickScriptProcessorBufferSize(this._audioCtx.sampleRate);
    const node = this._audioCtx.createScriptProcessor(bufferSize, 1, 1);

    node.onaudioprocess = (e) => {
      const input = e.inputBuffer.getChannelData(0);
      this._onAudioFrame(input);
    };

    this._sourceNode.connect(node);
    // 有些瀏覽器需要接到 destination 圖形 onaudioprocess 才會持續觸發，
    // 但直連會造成麥克風回音，改經 gain=0 的節點靜音輸出
    const silentGain = this._audioCtx.createGain();
    silentGain.gain.value = 0;
    node.connect(silentGain);
    silentGain.connect(this._audioCtx.destination);
    this._silentGain = silentGain;
    this._processorNode = node;
  }

  _onAudioFrame(inputFloat32) {
    if (!this.isRunning()) return;

    // 正常路徑 resampler 為 null：AudioContext 已經是 16 kHz，直接 pass-through。
    const data = this._resampler
      ? this._resampler.process(inputFloat32)
      : inputFloat32;

    // 累積 buffer
    this._buffer.push(...data);

    this._drainFullChunks();
  }

  /** 累積量夠一個 chunk 就吐；不足的留在 buffer 等下一 frame（收尾由 _flushTail 處理）。 */
  _drainFullChunks() {
    const chunkSize = this._chunkSize();

    while (this._buffer.length >= chunkSize) {
      const chunk = this._buffer.slice(0, chunkSize);
      this._buffer = this._buffer.slice(chunkSize);

      this._emitChunk(new Float32Array(chunk));
    }
  }

  /**
   * 收尾把未滿一個 chunk 的尾音送出。
   *
   * 送的是「不足長度的 chunk」而不是補零到 chunkSize：File Mode 的
   * _drainUnsentPcmBeforeEos 一直都是這樣送最後一包（end 取 min(start+chunkSize,
   * pcm.length)），KKASR 收變長 chunk 是線上已驗證的行為；補零反而會在句尾灌進
   * 最多 199 ms 的人造靜音。
   */
  _flushTail() {
    if (!this.isRunning()) return;

    // fallback 路徑時還有一段真實音訊卡在 FIR 的群延遲裡，一併推出來。
    if (this._resampler) {
      const tail = this._resampler.flush();
      if (tail.length > 0) this._buffer.push(...tail);
      // flush 之後才 reset：跨 frame 狀態屬於這一場收音，留著會讓下一場
      // （stop 之後再 start，mic stream 並沒有關）開頭混進上一場的殘響。
      this._resampler.reset();
    }

    this._drainFullChunks();

    if (this._buffer.length === 0) return;
    this._emitChunk(new Float32Array(this._buffer));
    this._buffer = [];
  }

  _chunkSize() {
    return Math.floor(this._targetSampleRate * this._chunkSec);
  }
}

/**
 * 依 AudioContext 的取樣率挑 ScriptProcessor 的 buffer 大小。
 *
 * 固定 4096 是在 48 kHz 前提下訂的（≈85 ms 一個 frame）。改成 16 kHz context 之後
 * 同樣的 4096 會變成 256 ms，等於白白多 170 ms 的音訊延遲——語音指令的反應會慢一拍。
 * 這裡改成「維持約 85 ms 的 frame 長度」再取最近的 2 次方（規範要求 256–16384 的
 * 2 次方）：48 kHz 仍然算出 4096（行為不變），16 kHz 算出 1024。
 *
 * 不再更小是因為 ScriptProcessor 跑在主執行緒，frame 越短、主執行緒忙碌時越容易
 * 掉 frame；1024 在 16 kHz 下仍有 64 ms 的處理餘裕。
 *
 * @param {number} sampleRate
 * @returns {number}
 */
export function pickScriptProcessorBufferSize(sampleRate) {
  const TARGET_FRAME_SEC = 4096 / 48000;
  const rate = Number(sampleRate) > 0 ? Number(sampleRate) : 48000;
  const exponent = Math.round(Math.log2(rate * TARGET_FRAME_SEC));
  const clamped = Math.min(14, Math.max(8, exponent));
  return Math.pow(2, clamped);
}
